import { SimpleGrid, Stack, StatCard, Text, type StatDelta } from '@grovus/design-system'
import { delta, type SegmentCoverage, type TileMeasure, type Withheld } from '../lib/delta'
import { formatRange } from '../lib/period'
import type { Dict } from '../i18n'
import { fill, money, NO_VALUE, num, ratio } from './format'
import type { ObservedTotals, TileKey, Totals } from './partition'
import { conversionUnit, type ViewContext } from './view'

/** The previous period's totals over the same segment, and whether they can be compared. */
export interface TotalsComparison {
  previous: ObservedTotals
  coverage: SegmentCoverage
  period: { from: string; to: string }
}

interface Props {
  totals: Totals
  ctx: ViewContext
  /** Absent when no comparison was asked for or the server predates it: no delta slot. */
  comparison?: TotalsComparison | null
  /**
   * Some delivery in scope is unseen (D8). Every creative then carries the mark, so a
   * count of them would say nothing: the tile names the fact and points at the scope
   * notices above, which state each cause.
   */
  scopePartial?: boolean
}

function withheldText(reason: Withheld, t: Dict, provisionalAt: number): string {
  switch (reason) {
    case 'inProgress': return t.notComparedInProgress
    case 'unverified': return t.notComparedUnverified
    case 'gaps': return t.notComparedGaps
    case 'partial': return t.withheldPartial
    case 'thin': return fill(t.withheldThin, { n: provisionalAt })
    case 'noBase': return t.withheldNoBase
  }
}

/**
 * The six headline figures, read from the segment, and against the previous period when
 * the response carries one.
 *
 * A delta is a number only when the comparison can stand behind it (`lib/delta.ts`); a
 * withheld one renders the no-value glyph with the reason as its name, because a zero
 * delta reads as "flat", which is a different claim from "not known". A ratio whose
 * denominator is zero renders the no-value glyph for the same reason, and so does a
 * measure the segment never reported: the sums are nullable, an unreported measure is
 * the glyph rather than a fabricated zero, and the sub ratios refuse to compute on top
 * of one. The rankable tile counts creatives rather than describing the account and
 * carries no delta.
 */
export function TotalsRow({ totals, ctx, comparison = null, scopePartial = false }: Props) {
  const { t, lang, currency } = ctx
  const deltaOf = (measure: TileMeasure): StatDelta | undefined => {
    if (!comparison) return undefined
    const d = delta(measure, totals, comparison.previous, { coverage: comparison.coverage, thresholds: ctx.thresholds })
    if (d === null) return undefined
    return 'withheld' in d
      ? { withheld: withheldText(d.withheld, t, ctx.thresholds.provisionalAt), glyph: NO_VALUE }
      : d
  }
  const caption = !comparison || comparison.coverage === 'empty'
    ? null
    : comparison.coverage === null
      ? fill(t.compareCaption, { r: formatRange(comparison.period.from, comparison.period.to, lang) })
      : withheldText(comparison.coverage, t, ctx.thresholds.provisionalAt)
  const cpa =
    totals.spend !== null && totals.conversions !== null && totals.conversions > 0
      ? totals.spend / totals.conversions
      : null
  const cpm =
    totals.spend !== null && totals.impressions !== null && totals.impressions > 0
      ? (totals.spend / totals.impressions) * 1000
      : null
  // The reason a tile is partial, as visible text: a `title` reaches neither a touch
  // device nor a screenshot. `on` is today's flags for the tile's value and its sub line.
  const partialOf = (tile: TileKey, on: boolean) => {
    if (!on) return undefined
    const reason = scopePartial
      ? t.partialReasonScope
      : fill(t.partialReasonCreatives, { c: totals.partialCreatives[tile], t: totals.creatives })
    return {
      label: t.partialWord,
      name: t.partialMeasure,
      reason,
      hint: { text: t.partialMeasure, name: t.partialHintName },
    }
  }
  const tiles = [
    {
      key: 'spend',
      value: money(totals.spend, currency, lang),
      label: t.spend,
      sub: fill(t.subAdsCreatives, { a: totals.ads, c: totals.creatives }),
      partial: partialOf('spend', totals.partial.spend),
      delta: deltaOf('spend'),
    },
    {
      key: 'conversions',
      value: num(totals.conversions, lang),
      label: t.conversions,
      // The unit pluralizes from zero when the measure went unreported: the glyph
      // carries the missingness, the unit keeps the sentence grammatical.
      sub: `${num(totals.conversions, lang)} ${conversionUnit(totals.conversions ?? 0, ctx)}`,
      partial: partialOf('conversions', totals.partial.conversions),
      delta: deltaOf('conversions'),
    },
    {
      key: 'roas',
      value: ratio(totals.roas, lang),
      label: t.roasCol,
      sub: fill(t.subCpa, { v: money(cpa, currency, lang, 2) }),
      hint: { text: t.hintRoas, name: t.hintNameRoas },
      partial: partialOf('roas', totals.partial.conversionValue || totals.partial.spend || totals.partial.conversions),
      delta: deltaOf('roas'),
    },
    {
      key: 'aov',
      value: money(totals.aov, currency, lang, 2),
      label: t.aovCol,
      sub: fill(t.subValue, { v: money(totals.conversionValue, currency, lang) }),
      hint: { text: t.hintAov, name: t.hintNameAov },
      partial: partialOf('aov', totals.partial.conversionValue || totals.partial.conversions),
      delta: deltaOf('aov'),
    },
    {
      key: 'impressions',
      value: num(totals.impressions, lang),
      label: t.impressions,
      sub: fill(t.subCpm, { v: money(cpm, currency, lang, 2) }),
      partial: partialOf('impressions', totals.partial.spend || totals.partial.impressions),
      delta: deltaOf('impressions'),
    },
    {
      key: 'rankable',
      value: fill(t.rankableSub, { c: totals.rankable, t: totals.creatives }),
      label: t.rankable,
      sub: fill(t.subBelow, { c: totals.thin, n: ctx.thresholds.directionAt }),
      // The mark that took the banner's dropped tail: how many creatives clear
      // the provisional line, and why the rest compare pooled (D2).
      hint: { text: t.hintRankable, name: t.hintNameRankable },
      partial: undefined,
      delta: undefined,
    },
  ]

  const grid = (
    <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md" data-testid="creatives-totals">
      {tiles.map((tile) => (
        // `display: contents` keeps the card a direct grid item, as it was before the wrapper.
        <div key={tile.key} data-testid={`tile-${tile.key}`} style={{ display: 'contents' }}>
          <StatCard
            value={tile.value}
            label={tile.label}
            sub={tile.sub}
            hint={tile.hint}
            partial={tile.partial}
            delta={tile.delta}
          />
        </div>
      ))}
    </SimpleGrid>
  )
  if (caption === null) return grid
  return (
    <Stack gap="xs">
      <Text size="sm" className="grv-muted" data-testid="compare-caption">{caption}</Text>
      {grid}
    </Stack>
  )
}
